import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import Link from 'next/link';

import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { SegmentPage } from '@flipster/features/src/misc/segment/segment-page';
import { OrderAttributionMapper, orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { zeroSpreadSymbolsBunja } from '@flipster/features/src/shared/zero-spread/state';
import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { SpinnerBox } from '@flipster/react/design-system/spinner';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';
import { ZeroSpreadEvent } from '@flipster/utils/segment/events/zero-spread';

export const ZeroSpread = () => {
  const setOrderAttribution = useSetAtom(orderAttributionAtom);

  const { zeroSpreadSymbolsAtom, isLoadingAtom } = useBunja(zeroSpreadSymbolsBunja);
  const zeroSpreadSymbols = useAtomValue(zeroSpreadSymbolsAtom);
  const isLoading = useAtomValue(isLoadingAtom);

  return (
    <>
      <SegmentPage name='market_zero_spread_section_view' />
      <section
        className={css({
          pl: { base: 16, tablet: 0 },
          pb: { base: 32, tablet: 80 },
          gap: { base: 16, tablet: 24 },
        })}
      >
        <h2 className={hstack({ textStyle: 'Title/L', color: 'content.default.level-5', gap: 8 })}>
          <FormattedMessage id='Market.r250919.Zero_spread' />
        </h2>
        {isLoading && <SpinnerBox />}
        {!isLoading && (
          <>
            <div
              className={css({
                overflowX: 'scroll',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              })}
            >
              <div
                className={css({
                  w: 'desktopMaxWidth',
                  overflow: 'visible',
                  mt: 8,
                  mb: 16,
                })}
              >
                {zeroSpreadSymbols?.map((symbol) => (
                  <Link
                    href={`/trade/perpetual/${symbol}`}
                    className={css({
                      display: 'inline-block',
                      bgColor: 'background.default.level-3',
                      py: 4,
                      pl: 4,
                      pr: 12,
                      mr: 8,
                      mt: 8,
                      lineHeight: 0,
                      rounded: 1000,
                    })}
                    onClick={() => {
                      setOrderAttribution(OrderAttributionMapper.market.carouselZeroSpread);
                      segment.track(TradeEvent.TradeCryptoClick, {
                        trade_type: 'perpetual',
                        symbol,
                        event_location: 'zero_spread',
                      });
                    }}
                    key={symbol}
                  >
                    <SymbolDisplay
                      symbol={symbol}
                      mainLabel={
                        <SymbolDisplay.Label
                          value='shortName'
                          css={{ textStyle: 'Text/M/Regular', color: 'content.default.level-5' }}
                        />
                      }
                      icon={<SymbolDisplay.Icon size={24} space={4} />}
                    />
                  </Link>
                ))}
              </div>
            </div>
            <div className={css({ textAlign: 'center', pr: 16 })}>
              <Button
                as='a'
                href='/zero-spread'
                hierarchy='secondary'
                label={<FormattedMessage id='Common.r250401.Learn_more' />}
                color='neutral'
                size='small'
                css={{ w: '100%', maxW: 343 }}
                onClick={() => {
                  segment.track(ZeroSpreadEvent.ZeroSpreadLearnMoreClick);
                }}
              />
            </div>
          </>
        )}
      </section>
    </>
  );
};
